/**
 * @fileoverview Checkout Form Utility Functions
 *
 * This file contains pure utility functions for checkout form operations,
 * including field grouping, validation helpers, and data transformations.
 *
 * Core Utilities:
 * - groupCheckoutFields: Organizes form fields by their group labels
 * - isUSASelected: Country-specific logic for conditional field display
 * - pascalToCamelCase: API field name transformation utility
 * - validateRequiredFields: Basic required field validation helper
 *
 * Key Features:
 * - Pure functions with no side effects for predictable behavior
 * - Country-specific logic handling (USA state selection)
 * - Field metadata processing and organization
 * - String case conversion for API compatibility
 * - Validation support functions for form processing
 *
 * Country Logic:
 * - Intelligent USA detection from country lists
 * - Flexible matching for different country label formats
 * - Support for both string and numeric country identifiers
 */

import { CheckoutFieldMetadata, CheckoutApiResponse } from "./CheckoutForm.types";

/**
 * Groups checkout fields by their groupLabel for organized form rendering
 *
 * @param metadata - Array of field metadata from the API
 * @returns Object with group labels as keys and arrays of fields as values
 */
export const groupCheckoutFields = (metadata: CheckoutFieldMetadata[]) => {
  return metadata.reduce(
    (groups, field) => {
      if (!groups[field.groupLabel]) {
        groups[field.groupLabel] = [];
      }
      groups[field.groupLabel].push(field);
      return groups;
    },
    {} as Record<string, CheckoutFieldMetadata[]>
  );
};

/**
 * Checks if the selected country is USA based on current form data
 *
 * Uses intelligent matching to identify USA from various label formats
 * (United States, USA, US, America) and handles both string/number values.
 *
 * @param checkoutData - Complete API response with country options
 * @param getFieldValue - Function to retrieve current field values
 * @returns True if USA is selected, false otherwise
 */
export const isUSASelected = (
  checkoutData: CheckoutApiResponse,
  getFieldValue: (field: CheckoutFieldMetadata) => any
): boolean => {
  const countryValue = getFieldValue({ name: "CountriesId" } as CheckoutFieldMetadata);

  // If no country selected, return false
  if (!countryValue) return false;

  // Find the USA country in the countries list by looking for labels that indicate USA
  const countryList = checkoutData.details.listOfCountries || [];
  const usaCountry = countryList.find(
    (country) =>
      country.label?.toLowerCase().includes("united states") ||
      country.label?.toLowerCase().includes("usa") ||
      country.label?.toLowerCase() === "us"
  );

  // Check if the selected country value matches the USA country value
  // Use loose equality (==) to handle string/number type mismatches
  return Boolean(usaCountry && countryValue == usaCountry.value);
};

/**
 * Converts PascalCase strings to camelCase for API field mapping
 *
 * @param str - PascalCase string to convert
 * @returns camelCase version of the string
 */
export const pascalToCamelCase = (str: string): string => {
  return str.charAt(0).toLowerCase() + str.slice(1);
};

/**
 * Checks if the auto-select site feature is enabled based on checkout data
 *
 * @param checkoutData - Complete API response with configuration
 * @returns True if site selection mode is active, false otherwise
 */
export const isAutoSelectSiteEnabled = (checkoutData: CheckoutApiResponse): boolean => {
  return Boolean(checkoutData?.details?.autoSelectSite);
};

/**
 * Gets the selected site object from the checkout data based on site ID
 *
 * @param checkoutData - Complete API response with site options
 * @param siteId - Selected site identifier
 * @returns Site object or null if not found
 */
export const getSelectedSite = (checkoutData: CheckoutApiResponse, siteId: number | string) => {
  if (!checkoutData?.details?.listOfSites || !siteId) return null;

  return (
    checkoutData.details.listOfSites.find(
      (site) => site.sitesId.toString() === siteId.toString()
    ) || null
  );
};

/**
 * Populates address fields based on selected site data
 * Returns an object with field names and their values from the site
 *
 * @param site - Selected site object containing address information
 * @returns Object with field values for form population
 */
export const populateAddressFromSite = (site: any) => {
  if (!site) return {};

  return {
    SitesName: site.sitesName || "",
    AddressLine1: site.addressLine1 || "",
    AddressLine2: site.addressLine2 || "",
    AddressLine3: site.addressLine3 || "",
    AddressLine4: site.addressLine4 || "",
    City: site.city || "",
    StateProvince: site.stateProvince || "",
    USStatesID: site.usStatesID || "",
    PostalZipCode: site.postalZipCode || "",
    CountriesId: site.countriesId || "",
  };
};

/**
 * Checks if address fields should be disabled based on auto-select site mode
 *
 * @param checkoutData - Complete API response
 * @param fieldName - Name of the field to check
 * @returns True if field should be disabled, false otherwise
 */
export const shouldDisableAddressField = (
  checkoutData: CheckoutApiResponse,
  fieldName: string
): boolean => {
  if (!isAutoSelectSiteEnabled(checkoutData)) return false;

  // List of address fields that should be disabled when site is selected
  const addressFields = [
    "SitesName",
    "AddressLine1",
    "AddressLine2",
    "AddressLine3",
    "AddressLine4",
    "City",
    "StateProvince",
    "USStatesID",
    "PostalZipCode",
    "CountriesId",
  ];

  return addressFields.includes(fieldName);
};

/**
 * Validates required fields in form data and returns error messages
 *
 * @param formData - Current form data to validate
 * @param metadata - Field metadata containing required field information
 * @returns Array of error messages for missing required fields
 */
export const validateRequiredFields = (
  formData: Record<string, any>,
  metadata: CheckoutFieldMetadata[]
): string[] => {
  const errors: string[] = [];

  metadata.forEach((field) => {
    if (field.bRequired && (!formData[field.name] || formData[field.name] === "")) {
      errors.push(`${field.label} is required`);
    }
  });

  return errors;
};
